/**
 * BuyLanternsModal — Stripe Checkout entry surface.
 *
 * Phase C3 of the season-lantern buildout. Server endpoints
 * (`POST /api/payments/checkout`, the webhook, and the history feed)
 * already ship in lambdas/payments. The client only needs to:
 *   1. show the SKU catalogue (5 lantern bundles + 1 premium pass),
 *   2. POST the chosen SKU to /api/payments/checkout,
 *   3. redirect the browser to the Stripe-hosted URL.
 *
 * The webhook is the source of truth for granting lanterns/premium —
 * this modal NEVER mutates balances directly. After Stripe redirects
 * the user back to `/payment-success?cs=...`, `PaymentSuccess.tsx`
 * polls /api/whoami for the balance bump.
 */
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ApiError, createCheckoutSession } from '../../lib/api';
import { useEscapeClose } from '../../lib/useEscapeClose';
import { track } from '../../lib/analytics';

/** Mirror of STRIPE_SKUS in lambdas/payments — kept here ONLY for
 *  display. The server is the authoritative source: it re-validates the
 *  SKU, recomputes the price, and rejects anything not in its own table.
 *  If these drift, server-side wins; the client just shows what it has. */
interface BundleTile {
  sku: string;
  name: string;
  /** Lanterns granted on webhook completion. 0 for the premium pass. */
  lanterns: number;
  cents: number;
  /** Marketing flair displayed on the tile. */
  badge?: 'best-value' | 'popular';
  /** Premium pass SKU is hidden when the player already owns this season's pass. */
  isPremiumPass?: boolean;
  /** Optional season tag for the premium pass. */
  premiumSeason?: number;
}

const BUNDLES: BundleTile[] = [
  { sku: 'wisp',    name: 'Wisp Bundle',    lanterns: 100,  cents: 99   },
  { sku: 'glow',    name: 'Glow Bundle',    lanterns: 550,  cents: 499  },
  { sku: 'beacon',  name: 'Beacon Bundle',  lanterns: 1200, cents: 999, badge: 'popular'    },
  { sku: 'pyre',    name: 'Pyre Bundle',    lanterns: 2700, cents: 1999 },
  { sku: 'bonfire', name: 'Bonfire Bundle', lanterns: 7500, cents: 4999, badge: 'best-value' },
];

const PREMIUM_PASS: BundleTile = {
  sku: 'pass-s1',
  name: 'Season 1 Premium Pass',
  lanterns: 0,
  cents: 999,
  isPremiumPass: true,
  premiumSeason: 1,
};

interface Props {
  onClose: () => void;
  /** When true, hide the premium-pass tile (player already owns it for the
   *  current season). The SeasonPassPanel passes this through; ad-hoc Home
   *  callers can leave it undefined and the tile shows by default. */
  hasPremium?: boolean;
}

export function BuyLanternsModal({ onClose, hasPremium }: Props) {
  const [pendingSku, setPendingSku] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscapeClose(true, onClose);

  // Lock body scroll while the modal is up so the parchment under it
  // doesn't scroll behind the user when they swipe on the tile list.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const tiles = useMemo<BundleTile[]>(() => {
    return hasPremium ? BUNDLES : [...BUNDLES, PREMIUM_PASS];
  }, [hasPremium]);

  async function onBuy(sku: string) {
    if (pendingSku) return;
    const tile = [...BUNDLES, PREMIUM_PASS].find((b) => b.sku === sku);
    track('bundle_click', { sku, name: tile?.name, lanterns: tile?.lanterns, cents: tile?.cents });
    setPendingSku(sku);
    setError(null);
    try {
      const { checkoutUrl } = await createCheckoutSession(sku);
      track('checkout_start', { sku, name: tile?.name, cents: tile?.cents });
      // Stash the chosen SKU + expected lantern grant so PaymentSuccess
      // can announce "you got +N" even if the webhook wins the race
      // against /api/whoami's first call (in which case the balance has
      // already moved by the time we capture a "baseline"). Idempotent
      // and self-cleared by the success page once consumed.
      try {
        if (tile) {
          localStorage.setItem('tt_pending_purchase', JSON.stringify({
            sku: tile.sku,
            name: tile.name,
            lanterns: tile.lanterns,
            startedAt: Date.now(),
          }));
        }
      } catch { /* localStorage disabled — the page will fall back to balance polling */ }
      // Hard redirect — Stripe Checkout is hosted off-domain. Don't
      // bother resetting pendingSku, the page is about to unload.
      window.location.href = checkoutUrl;
    } catch (e) {
      const msg = e instanceof ApiError
        ? e.code === 'payments-disabled'
            ? 'Real-money purchases aren’t set up yet — earn lanterns by claiming Season Pass tiers, and unlock the Premium Pass with 1,000 lanterns.'
        : e.code === 'unknown-sku' ? 'That bundle is no longer available.'
        : e.code === 'no-actor' ? 'Pick a character first.'
        : `Could not start checkout (${e.status}).`
        : 'Could not reach the server. Try again.';
      setError(msg);
      setPendingSku(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Buy Lanterns"
      className="buy-lanterns-overlay"
      onClick={(e) => { if (e.target === e.currentTarget && !pendingSku) onClose(); }}
    >
      <div className="buy-lanterns-modal">
        <header className="buy-lanterns-header">
          <h2 className="display buy-lanterns-title">
            <span className="buy-lanterns-glyph" aria-hidden="true">🏮</span>
            Buy Lanterns
          </h2>
          <button
            type="button"
            className="buy-lanterns-close"
            onClick={onClose}
            disabled={pendingSku !== null}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        <p className="muted buy-lanterns-blurb">
          Lanterns unlock the lantern shop, premium-pass cosmetics, and seasonal
          treats. Payments are processed securely by Stripe — your balance updates
          here within a few seconds.
        </p>

        {error && (
          <div role="alert" className="buy-lanterns-error">{error}</div>
        )}

        <div className="buy-lanterns-grid">
          {tiles.map((t) => (
            <BundleCard
              key={t.sku}
              tile={t}
              busy={pendingSku === t.sku}
              disabled={pendingSku !== null && pendingSku !== t.sku}
              onBuy={() => void onBuy(t.sku)}
            />
          ))}
        </div>

        <p className="muted buy-lanterns-fineprint">
          One-time purchase. Lanterns are non-refundable, do not expire, and
          remain on your character even after the season ends.
        </p>
      </div>
    </div>
  );
}

interface BundleCardProps {
  tile: BundleTile;
  busy: boolean;
  disabled: boolean;
  onBuy: () => void;
}

function BundleCard({ tile, busy, disabled, onBuy }: BundleCardProps) {
  const dollars = (tile.cents / 100).toFixed(2);
  const cls = [
    'bundle-tile',
    tile.badge === 'best-value' ? 'best-value' : '',
    tile.badge === 'popular' ? 'popular' : '',
    tile.isPremiumPass ? 'premium-pass' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} aria-label={tile.name}>
      {tile.badge === 'best-value' && (
        <span className="bundle-tile-badge best-value-badge">Best Value</span>
      )}
      {tile.badge === 'popular' && (
        <span className="bundle-tile-badge popular-badge">Most Popular</span>
      )}

      <div className="bundle-tile-icon" aria-hidden="true">
        {tile.isPremiumPass ? '✨' : '🏮'}
      </div>

      <div className="bundle-tile-name">{tile.name}</div>

      <div className="bundle-tile-amount">
        {tile.isPremiumPass
          ? <span className="bundle-tile-unlock">Unlock Premium</span>
          : <><strong>{tile.lanterns.toLocaleString()}</strong> lanterns</>}
      </div>

      <div className="bundle-tile-price">${dollars}</div>

      <button
        type="button"
        className="btn primary bundle-tile-buy"
        onClick={onBuy}
        disabled={busy || disabled}
      >
        {busy
          ? <><span className="bundle-spinner" aria-hidden="true" />Redirecting…</>
          : tile.isPremiumPass ? 'Unlock' : 'Buy'}
      </button>
    </div>
  );
}
